'use client';
import React from 'react';
import { useForm } from 'react-hook-form';
import { FileText, ShoppingBag, Utensils, Zap, Smartphone, Package, ArrowRight, ArrowLeft, AlertTriangle } from 'lucide-react';
import type { DeliveryFormData } from './BookDeliveryWizard';

const PACKAGE_TYPES = [
  { id: 'Document', label: 'Hati / Nyaraka', sublabel: 'Documents', icon: FileText, desc: 'Hati, barua, nyaraka muhimu', color: 'bg-blue-50 border-blue-200 text-blue-700' },
  { id: 'Parcel', label: 'Kifurushi', sublabel: 'Parcel / Box', icon: Package, desc: 'Vitu vya jumla, zawadi, bidhaa', color: 'bg-orange-50 border-orange-200 text-orange-700' },
  { id: 'Food', label: 'Chakula', sublabel: 'Food & Drinks', icon: Utensils, desc: 'Chakula, vinywaji, mikate', color: 'bg-green-50 border-green-200 text-green-700' },
  { id: 'Fragile', label: 'Kioo / Brittle', sublabel: 'Fragile Items', icon: Zap, desc: 'Bidhaa zinazovunjika — kioo, vyombo', color: 'bg-red-50 border-red-200 text-red-700' },
  { id: 'Electronics', label: 'Elektroniki', sublabel: 'Electronics', icon: Smartphone, desc: 'Simu, laptop, vifaa vya umeme', color: 'bg-purple-50 border-purple-200 text-purple-700' },
  { id: 'Oversized', label: 'Kubwa Sana', sublabel: 'Oversized', icon: ShoppingBag, desc: 'Vitu vikubwa — fanicha, magurudumu', color: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
];

interface StepPackageProps {
  formData: DeliveryFormData;
  updateForm: (partial: Partial<DeliveryFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

interface PackageFormValues {
  packageType: string;
  packageDescription: string;
  weight: string;
  fragile: boolean;
  notes: string;
}

export default function StepPackage({ formData, updateForm, onNext, onBack }: StepPackageProps) {
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<PackageFormValues>({
    defaultValues: {
      packageType: formData.packageType,
      packageDescription: formData.packageDescription,
      weight: formData.weight,
      fragile: formData.fragile,
      notes: formData.notes,
    },
  });

  const selectedType = watch('packageType');

  const onSubmit = (data: PackageFormValues) => {
    updateForm(data);
    onNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* Package Type Grid */}
      <div className="card-elevated p-5">
        <h3 className="text-base font-semibold text-foreground mb-1">Aina ya Kifurushi</h3>
        <p className="text-xs text-muted-foreground mb-4">Chagua aina inayofaa zaidi kwa vitu vyako</p>

        <input type="hidden" {...register('packageType', { required: 'Tafadhali chagua aina ya kifurushi' })} />

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {PACKAGE_TYPES.map(pkg => (
            <button
              key={`pkg-${pkg.id}`}
              type="button"
              onClick={() => setValue('packageType', pkg.id, { shouldValidate: true })}
              className={`relative p-3.5 rounded-xl border-2 text-left transition-all duration-150 ${
                selectedType === pkg.id
                  ? 'border-primary bg-primary/5 shadow-md'
                  : `border-border hover:border-primary/40 bg-card ${pkg.color.includes('bg') ? '' : ''}`
              }`}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${pkg.color}`}>
                <pkg.icon size={18} />
              </div>
              <p className="text-sm font-semibold text-foreground leading-tight">{pkg.label}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{pkg.sublabel}</p>
              {selectedType === pkg.id && (
                <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>
        {errors.packageType && <p className="text-xs text-red-600 mt-2">{errors.packageType.message}</p>}

        {/* Description of selected type */}
        {selectedType && (
          <div className="mt-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
            <p className="text-xs text-primary font-medium">
              {PACKAGE_TYPES.find(p => p.id === selectedType)?.desc}
            </p>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="card-elevated p-5 space-y-4">
        <h3 className="text-base font-semibold text-foreground">Maelezo ya Kifurushi</h3>

        <div>
          <label className="block text-xs font-semibold text-foreground mb-1 tracking-wide">
            Maelezo Mafupi <span className="text-red-500">*</span>
          </label>
          <p className="text-[11px] text-muted-foreground mb-1.5">Eleza vitu vilivyomo — itasaidia rider kushughulikia kwa uangalifu</p>
          <textarea
            {...register('packageDescription', { required: 'Tafadhali eleza vitu vilivyomo' })}
            rows={3}
            placeholder="e.g. Simu moja ya Samsung, imefungwa vizuri katika boksi la asili"
            className={`input-base w-full px-4 py-2.5 text-sm resize-none ${errors.packageDescription ? 'input-error' : ''}`}
          />
          {errors.packageDescription && <p className="text-xs text-red-600 mt-1">{errors.packageDescription.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1 tracking-wide">Uzito (kg)</label>
            <p className="text-[11px] text-muted-foreground mb-1.5">Uzito wa takriban</p>
            <input
              {...register('weight')}
              type="number"
              step="0.5"
              min="0"
              placeholder="e.g. 2.5"
              className="input-base w-full px-4 py-2.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1 tracking-wide">Hali ya Kifurushi</label>
            <p className="text-[11px] text-muted-foreground mb-1.5">Je, ni kitu kinachoweza kuvunjika?</p>
            <label className="flex items-center gap-2.5 mt-3 cursor-pointer">
              <div className="relative">
                <input type="checkbox" {...register('fragile')} className="sr-only peer" />
                <div className="w-10 h-6 bg-muted rounded-full peer-checked:bg-primary transition-colors duration-200" />
                <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground flex items-center gap-1">
                  <AlertTriangle size={13} className="text-amber-500" />
                  Fragile
                </p>
              </div>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-foreground mb-1 tracking-wide">Maelezo Mengine (Hiari)</label>
          <textarea
            {...register('notes')}
            rows={2}
            placeholder="e.g. Piga simu kabla ya kuweka, acha kwa jirani kama hayupo"
            className="input-base w-full px-4 py-2.5 text-sm resize-none"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 btn-secondary py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
        >
          <ArrowLeft size={16} />
          Rudi Nyuma
        </button>
        <button
          type="submit"
          className="flex-1 btn-primary py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
        >
          Angalia Bei
          <ArrowRight size={16} />
        </button>
      </div>
    </form>
  );
}